/**
 * Communities (Сообщества) — interest clubs and colleague groups.
 *
 * Tamagui port of the handoff `CommunitiesScreen` (screen-life.jsx):
 *  • search by name
 *  • "My communities" grid (the ones you've joined)
 *  • "Discover" grid (the rest) with a Join / Joined toggle
 *
 * Renders live `GET /communities` only and drives the Join/Leave toggle through
 * `POST/DELETE /communities/:id/join`; membership comes from the server. An empty
 * or unreachable response shows the empty state (no demo fallback).
 */
import { useMemo, useState } from 'react'
import { Input, Stack, Text, XStack, YStack } from 'tamagui'

import { useApp } from '../../src/i18n'
import { useCommunities, useToggleCommunity } from '../../src/features/communities/useCommunities'
import type { Community } from '../../src/lib/types'
import { tone } from '../../src/theme/tones'
import { Button, Card, Enter, Icon, QueryState } from '../../src/ui'
import { Page } from '../../src/ui/Screen'

function CommunityCard({
  c,
  joined,
  busy,
  onToggle,
}: {
  c: Community
  joined: boolean
  busy?: boolean
  onToggle: () => void
}) {
  const { t, l } = useApp()
  const tn = tone(c.tone)
  return (
    <Card
      animation="quicker"
      flexGrow={1}
      flexBasis={200}
      minWidth={170}
      maxWidth={280}
      padding={18}
      alignItems="center"
      gap={10}
      hoverStyle={{ y: -2, shadowOpacity: 0.2, shadowRadius: 12 }}
    >
      <Stack width={52} height={52} borderRadius={12} backgroundColor={tn.soft} alignItems="center" justifyContent="center">
        <Icon name={c.icon} size={24} color={tn.text} />
      </Stack>
      <YStack alignItems="center" gap={2}>
        <Text fontSize={15} fontWeight="700" color="$color" textAlign="center">
          {t(c.name)}
        </Text>
        <Text fontSize={11} color="$muted">
          {c.members} {l('c.members')}
        </Text>
      </YStack>
      <Button
        size="sm"
        block
        variant={joined ? 'default' : 'primary'}
        onPress={busy ? undefined : onToggle}
        opacity={busy ? 0.6 : 1}
        cursor={busy ? 'default' : 'pointer'}
        icon={joined ? <Icon name="check" size={14} color="$primary" /> : undefined}
      >
        {joined ? l('c.joined') : l('c.join')}
      </Button>
    </Card>
  )
}

function Section({
  title,
  items,
  busy,
  onToggle,
}: {
  title: string
  items: Community[]
  busy?: boolean
  onToggle: (c: Community) => void
}) {
  if (items.length === 0) return null
  return (
    <YStack gap={14}>
      <Text fontSize={15} fontWeight="700" color="$color">
        {title}
      </Text>
      <XStack flexWrap="wrap" gap={16}>
        {items.map((c, i) => (
          <Enter key={c.id} index={Math.min(i, 11)} flexGrow={1} flexBasis={200} minWidth={170} maxWidth={280}>
            <CommunityCard c={c} joined={c.joined} busy={busy} onToggle={() => onToggle(c)} />
          </Enter>
        ))}
      </XStack>
    </YStack>
  )
}

export default function CommunitiesScreen() {
  const { t, l } = useApp()
  const [q, setQ] = useState('')

  const communitiesQuery = useCommunities()
  const toggleM = useToggleCommunity()
  const all: Community[] = communitiesQuery.data ?? []

  // Membership comes straight off the server (`joined`).
  const isJoined = (c: Community) => c.joined
  const toggle = (c: Community) => toggleM.mutate({ id: c.id, joined: c.joined })

  const filtered = useMemo(
    () => all.filter((c) => t(c.name).toLowerCase().includes(q.trim().toLowerCase())),
    [all, q, t],
  )
  const mine = filtered.filter((c) => isJoined(c))
  const rest = filtered.filter((c) => !isJoined(c))

  return (
    <Page title={l('comm.title')} subtitle={l('comm.subtitle')}>
      {/* search */}
      <XStack
        maxWidth={340}
        alignItems="center"
        gap={8}
        height={38}
        paddingHorizontal={11}
        borderWidth={1}
        borderColor="$borderColor"
        borderRadius={7}
        backgroundColor="$background"
      >
        <Icon name="search" size={15} color="$tertiary" />
        <Input
          unstyled
          flex={1}
          height={36}
          fontSize={14}
          color="$color"
          borderWidth={0}
          backgroundColor="transparent"
          placeholder={l('comm.searchPlaceholder')}
          placeholderTextColor="$tertiary"
          value={q}
          onChangeText={setQ}
        />
      </XStack>

      <QueryState q={communitiesQuery}>
      {filtered.length === 0 ? (
        <Card padding={48} alignItems="center">
          <Text fontSize={13} color="$muted">
            {l('comm.empty')}
          </Text>
        </Card>
      ) : (
        <>
          <Section title={l('comm.my')} items={mine} busy={toggleM.isPending} onToggle={toggle} />
          <Section title={l('comm.discover')} items={rest} busy={toggleM.isPending} onToggle={toggle} />
        </>
      )}
      </QueryState>

      <Stack height={8} />
    </Page>
  )
}
